// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type ExpandedState,
  type Row,
  createColumnHelper,
} from '@tanstack/react-table';
import { exportTableToExcel } from '@shared/ui-kit/utils/export-table-to-excel';
import { type MyIdeas } from '@shared/data-access/interfaces/common/my-ideas';
import { type SubRows } from '@shared/data-access/interfaces/common/my-investment';
import { type FC, useMemo } from 'react';
import { type LoadIdeaOnReduxProps } from '../hooks';
import { SharePortfolioButton } from './share-portfolio-button';
import { useAppSelector } from '@advisor-app/store/store';
import { selectUserData } from '@shared/data-access/slices/user';
import {
  AssetBrand,
  PortfolioActionsMenu,
  Table,
  CellData,
  WatchlistToggleButton,
} from '@shared/ui-kit/components';
import { useInvestmentPopup } from '@shared/tag-management/components/investment-popup';
import { useTranslation } from 'react-i18next';

export interface SavedPortfoliosTableProps {
  readonly data: Array<MyIdeas | SubRows>;
  readonly initialExpanded?: ExpandedState;
  readonly onLoad: ({ row, benchmark, flow }: LoadIdeaOnReduxProps) => void;
  readonly onEdit: (row: MyIdeas | SubRows) => void;
  readonly onDeleteIdea: (portfolio_id: number, nameIdea: string) => void;
}

const columnHelper = createColumnHelper<MyIdeas | SubRows>();

const handleOnExport = (subRows: any[], row: Row<MyIdeas | SubRows>) => {
  const filename = row.original.columnName;
  const idea = subRows.map(asset => ({
    ...asset.portfolio,
    benchmark: asset.benchmark,
  }));

  exportTableToExcel(idea, filename);
};

export const SavedPortfoliosTable: FC<SavedPortfoliosTableProps> = ({
  data,
  onLoad,
  onEdit,
  onDeleteIdea,
  initialExpanded,
}) => {
  const { t } = useTranslation();
  const { openInvestmentPopup } = useInvestmentPopup();

  const userData = useAppSelector(selectUserData);
  const columns = useMemo(
    () => [
      columnHelper.accessor('columnName', {
        cell: ({ getValue, row }) => (
          <div className="flex items-center gap-x-2 truncate">
            <Table.Expander expander={row} />
            {row.getCanExpand() ? (
              getValue()
            ) : (
              <AssetBrand
                ticker={row.original.columnName?.ticker}
                assetType={row.original.asset_class}
                label={row.original.columnName?.name}
                logoUrl={row.original.columnName?.url_logo}
                url={row.original.columnName?.url_morningstar}
                analyzePopup={openInvestmentPopup}
                watchlistButton={
                  <WatchlistToggleButton
                    ticker={row.original.columnName?.ticker}
                    assetType={row.original.asset_class}
                  />
                }
              />
            )}
          </div>
        ),
        header: t('common.name', { ns: 'common' }),
        maxSize: 220,
        enableHiding: false,
      }),
      columnHelper.accessor('asset_class', {
        header: t('glossary.vehicles', { ns: 'glossary' }),
        cell: ({ getValue }) => <span className="truncate">{getValue()}</span>,
      }),

      columnHelper.accessor('weight', {
        header: t('common.weight', { ns: 'common' }),
        meta: { alignment: 'end' },
        cell: ({ getValue }) => (
          <Table.Data
            value={getValue()}
            format="percentage"
            formatOptions={{ maximumFractionDigits: 1, minimumFractionDigits: 1 }}
          />
        ),
      }),

      columnHelper.display({
        id: 'ytd_return',
        header: t('glossary.investment-returns-ytd', { ns: 'glossary' }),
        meta: {
          tooltip: 'common.ytdReturn',
          alignment: 'end',
        },
        cell: ({ row }) =>
          !row.getCanExpand() && (
            <CellData
              value={row.original.columnName?.ytd_return}
              format="performance"
            />
          ),
      }),

      columnHelper.display({
        id: 'y1_return',
        header: t('glossary.investment-returns-1y', { ns: 'glossary' }),
        meta: {
          tooltip: 'common.oneYReturn',
          alignment: 'end',
        },
        cell: ({ row }) =>
          !row.getCanExpand() && (
            <CellData
              value={row.original.columnName?.y1_return}
              format="performance"
            />
          ),
      }),

      columnHelper.display({
        id: 'y3_return',
        header: t('glossary.investment-returns-3y', { ns: 'glossary' }),
        meta: {
          tooltip: 'common.threeYReturn',
          alignment: 'end',
        },
        cell: ({ row }) =>
          !row.getCanExpand() && (
            <CellData
              value={row.original.columnName?.y3_return}
              format="performance"
            />
          ),
      }),

      columnHelper.display({
        id: 'y5_return',
        header: t('glossary.investment-returns-5y', { ns: 'glossary' }),
        meta: {
          tooltip: 'common.fiveYReturn',
          alignment: 'end',
        },
        cell: ({ row }) =>
          !row.getCanExpand() && (
            <CellData
              value={row.original.columnName?.y5_return}
              format="performance"
            />
          ),
      }),

      columnHelper.display({
        id: 'y10_return',
        header: t('glossary.investment-returns-10y', { ns: 'glossary' }),
        meta: {
          tooltip: 'common.tenYReturn',
          alignment: 'end',
        },
        cell: ({ row }) =>
          !row.getCanExpand() && (
            <CellData
              value={row.original.columnName?.y10_return}
              format="performance"
            />
          ),
      }),

      columnHelper.display({
        id: 'y3_vol',
        header: t('glossary.3y-vol', { ns: 'glossary' }),
        meta: {
          tooltip: 'common.threeYVol',
          alignment: 'end',
        },
        cell: ({ row }) =>
          !row.getCanExpand() && (
            <Table.Data
              value={row.original.columnName?.y3_vol}
              format="percentage"
              emptyValue={-1}
            />
          ),
      }),

      columnHelper.display({
        id: 'max_draw_down',
        header: t('glossary.max-drawdown', { ns: 'glossary' }),
        meta: {
          tooltip: 'kpis.maxDrawdown',
          alignment: 'end',
        },
        cell: ({ row }) =>
          !row.getCanExpand() && (
            <Table.Data
              value={row.original.columnName?.max_draw_down}
              format="percentage"
              emptyValue={-1}
            />
          ),
      }),

      columnHelper.display({
        id: 'inception_return',
        header: t('glossary.investment-returns-inception', { ns: 'glossary' }),
        meta: {
          initiallyHidden: true,
          alignment: 'end',
        },
        cell: ({ row }) =>
          !row.getCanExpand() && (
            <CellData
              value={row.original.columnName?.inception_return}
              format="performance"
            />
          ),
      }),

      columnHelper.display({
        id: 'm1_return',
        header: t('glossary.investment-returns-m1', { ns: 'glossary' }),
        meta: {
          initiallyHidden: true,
          alignment: 'end',
        },
        cell: ({ row }) =>
          !row.getCanExpand() && (
            <CellData
              value={row.original.columnName?.m1_return}
              format="performance"
            />
          ),
      }),

      columnHelper.display({
        id: 'm3_return',
        header: t('glossary.investment-returns-m3', { ns: 'glossary' }),
        meta: {
          initiallyHidden: true,
          alignment: 'end',
        },
        cell: ({ row }) =>
          !row.getCanExpand() && (
            <CellData
              value={row.original.columnName?.m3_return}
              format="performance"
            />
          ),
      }),

      columnHelper.display({
        id: 'm6_return',
        header: t('glossary.investment-returns-m6', { ns: 'glossary' }),
        meta: {
          initiallyHidden: true,
          alignment: 'end',
        },
        cell: ({ row }) =>
          !row.getCanExpand() && (
            <CellData
              value={row.original.columnName?.m6_return}
              format="performance"
            />
          ),
      }),

      columnHelper.display({
        id: 'price',
        header: t('common.price', { ns: 'common' }),
        meta: {
          initiallyHidden: true,
          alignment: 'end',
        },
        cell: ({ row }) =>
          !row.getCanExpand() && (
            <CellData value={row.original.columnName?.price} format="currency" />
          ),
      }),

      columnHelper.display({
        id: 'price_as_of',
        header: t('research.price-as-of', { ns: 'research' }),
        meta: {
          initiallyHidden: true,
        },
        cell: ({ row }) =>
          !row.getCanExpand() && (
            <CellData value={row.original.columnName?.price_as_of} format="date" />
          ),
      }),

      columnHelper.accessor('creation_date', {
        header: t('common.date', { ns: 'common' }),
        cell: ({ getValue, row }) =>
          row.getCanExpand() && (
            <Table.Data value={getValue() as string} format="date" />
          ),
      }),
      columnHelper.display({
        id: 'options',
        enableSorting: false,
        minSize: 130,
        meta: { showColumnVisibilitySelector: true },
        cell: ({ row }) => {
          const isPortfolioOwner =
            'owner' in row.original && row.original.owner === userData?.nickname;

          return row.getCanExpand() ? (
            <div className="flex w-full items-center justify-end !gap-4">
              <PortfolioActionsMenu
                edit={isPortfolioOwner}
                share={isPortfolioOwner}
                shareWrapper={
                  <SharePortfolioButton portfolio={row.original as MyIdeas} />
                }
                analyze={['Mutual Fund', 'Stock', 'ETF'].includes(
                  row.original.asset_class,
                )}
                taxManager={['Stock', 'ETF'].includes(row.original.asset_class)}
                rebalance
                download
                delete={isPortfolioOwner}
                onDownload={() => handleOnExport(row.original.subRows, row)}
                onRebalance={() =>
                  onLoad({
                    benchmark: row.original.benchmark!,
                    flow: {
                      type: 'rebalance',
                      asset: '',
                    },
                    row: row.subRows,
                    portfolioName: row.original.portfolio!,
                  })
                }
                onTaxManager={() =>
                  onLoad({
                    benchmark: row.original.benchmark!,
                    flow: {
                      type: 'tax-harvesting',
                      asset: row.original.asset_class === 'ETF' ? 'etf' : 'stock',
                    },
                    row: row.subRows,
                    portfolioName: row.original.portfolio!,
                  })
                }
                onEdit={() => onEdit(row.original)}
                onAnalyze={() =>
                  onLoad({
                    benchmark: row.original.benchmark!,
                    flow: {
                      type: 'load',
                      asset:
                        row.original.asset_class === 'ETF'
                          ? 'etf'
                          : row.original.asset_class === 'Mutual Fund'
                          ? 'mutual_fund'
                          : 'stock',
                    },
                    row: row.subRows,
                    portfolioName: row.original.portfolio!,
                  })
                }
                onDelete={() =>
                  onDeleteIdea(
                    (row.original as any).portfolio_id,
                    row.original.columnName,
                  )
                }
                organizations={!!(row.original as MyIdeas)?.organizations?.length}
              />
            </div>
          ) : (
            ''
          );
        },
      }),
    ],
    [userData?.nickname, onEdit, onLoad, onDeleteIdea, openInvestmentPopup, t],
  );

  return (
    <div className="overflow-x-auto">
      <Table.Grouped
        columns={columns}
        data={data}
        initialExpanded={initialExpanded}
        className="w-full overflow-auto"
        state-id="saved-portfolios-table"
      />
    </div>
  );
};
